import React, { useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";

import BasicCRUD from './../core/basic-crud';
import { socketUseEffect } from "../../socket";
import BadgeStatusView from "./sub-pages/elements/BadgeStatusView";
import { useAuth } from "../../AuthContext";
import ProgressBar from "../page-elements/progress-bar";
import FadeAlert from "../core/fade-alert";

// ---------- i18n ----------
const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const dict = {
    en: {
        // page
        title: "Push Tests",
        subTitle: "add / edit push tests",
        homeURL: "/dashboard",
        addTitle: "Add Push Test",
        editTitle: "Edit Push Test",

        // columns
        colStatus: "Status",
        colName: "Name",
        colUpdates: "Updates",
        colContactGroup: "Contact Group",

        // cells
        unknownName: "Unknown Name",
        invalidDate: "Invalid Date",
        lastUpdate: "Last Update:",
        nextUpdate: "Next Update:",
        minutesShort: "m",
        secondsShort: "s",

        // form
        tabMain: "Main",
        fieldName: "Name",
        fieldContactGroup: "Contact Group",
        basicAuth: "Basic Authorization",
        login: "Login",
        password: "Password",
        yes: "yes",
        no: "no",
        choose: " -- choose -- ",

        // actions
        edit: "Edit",
        delete: "Delete",

        // quotas/plan
        noResources: "You don't have any available resources. Please change your pricing plan to get more items.",
        planExpired: "Your pricing plan has expired. Please renew or upgrade your plan to continue accessing this feature.",

        // delete dialog
        delAlert: "Deleting a custom test cannot be undone.",
        delText: "To confirm deletion, enter the name of the PUSH test in the next input field",
        delTitle: 'Delete PUSH test: "{name}"',

        // toasts
        toastEntity: "Test",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
    },

    ru: {
        title: "PUSH-тесты",
        subTitle: "добавление / редактирование PUSH-тестов",
        homeURL: "/dashboard",
        addTitle: "Добавить PUSH-тест",
        editTitle: "Редактировать PUSH-тест",

        colStatus: "Статус",
        colName: "Название",
        colUpdates: "Обновления",
        colContactGroup: "Группа контактов",

        unknownName: "Неизвестное имя",
        invalidDate: "Неверная дата",
        lastUpdate: "Последнее обновление:",
        nextUpdate: "Следующее обновление:",
        minutesShort: "м",
        secondsShort: "с",

        tabMain: "Основное",
        fieldName: "Название",
        fieldContactGroup: "Группа контактов",
        basicAuth: "Базовая авторизация",
        login: "Логин",
        password: "Пароль",
        yes: "да",
        no: "нет",
        choose: " -- выберите -- ",

        edit: "Редактировать",
        delete: "Удалить",

        noResources: "У вас нет доступных ресурсов. Пожалуйста, измените тарифный план, чтобы получить больше элементов.",
        planExpired: "Срок действия вашего тарифного плана истёк. Обновите или продлите план, чтобы продолжить пользоваться функцией.",

        delAlert: "Удаление пользовательского теста необратимо.",
        delText: "Чтобы подтвердить удаление, введите имя PUSH-теста в следующее поле ввода",
        delTitle: 'Удалить PUSH-тест: «{name}»',

        toastEntity: "Тест",
        createdOk: "успешно создан",
        updatedOk: "успешно обновлён",
        deletedOk: "успешно удалён",
    },

    ua: {
        title: "PUSH-тести",
        subTitle: "додавання / редагування PUSH-тестів",
        homeURL: "/dashboard",
        addTitle: "Додати PUSH-тест",
        editTitle: "Редагувати PUSH-тест",

        colStatus: "Статус",
        colName: "Назва",
        colUpdates: "Оновлення",
        colContactGroup: "Група контактів",

        unknownName: "Невідома назва",
        invalidDate: "Невірна дата",
        lastUpdate: "Останнє оновлення:",
        nextUpdate: "Наступне оновлення:",
        minutesShort: "хв",
        secondsShort: "с",

        tabMain: "Основне",
        fieldName: "Назва",
        fieldContactGroup: "Група контактів",
        basicAuth: "Базова авторизація",
        login: "Логін",
        password: "Пароль",
        yes: "так",
        no: "ні",
        choose: " -- оберіть -- ",

        edit: "Редагувати",
        delete: "Видалити",

        noResources: "У вас немає доступних ресурсів. Будь ласка, змініть тарифний план, щоб отримати більше елементів.",
        planExpired: "Строк дії вашого тарифного плану минув. Поновіть або оновіть план, щоб продовжити користування функцією.",

        delAlert: "Видалення користувацького тесту неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву PUSH-тесту в наступне поле вводу",
        delTitle: 'Видалити PUSH-тест: «{name}»',

        toastEntity: "Тест",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
    },
};

// ---------- component ----------
const CustomTest = ({ lang: langProp, ...props }) => {
    const { user } = useAuth();
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "custom_tests";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [contacts, setContacts] = useState([{ id: 0, name: t.choose }]);
    const [totalData, setTotalData] = useState([]);

    const listeners = [
        { name: "contacts", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setContacts },
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
    ];
    useEffect(() => socketUseEffect(listeners, user), [response?.status, response?.created, response?.updated, response?.delete]); // eslint-disable-line

    const formatStatus = (data) => <BadgeStatusView status={data.status} lang={lang} />;

    const CountdownTimer = ({ seconds }) => {
        const [remainingTime, setRemainingTime] = React.useState(seconds);

        React.useEffect(() => {
            const interval = setInterval(() => {
                setRemainingTime((prev) => Math.max(prev - 1, 0));
            }, 1000);
            return () => clearInterval(interval);
        }, []);

        if (remainingTime <= 0) return null;

        return (
            <span>
        {' '}(
                {Math.floor(remainingTime / 60)}{t.minutesShort} {remainingTime % 60}{t.secondsShort}
                )
      </span>
        );
    };

    const formatName = (data) => (
        <Link to={`/push-test/${data.id}/dashboard`} className="sidebar-link" data-text="Server Details">
            <span style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>{data.name || t.unknownName}</span>
        </Link>
    );

    const formatNameAndUpdates = (data) => {
        const name = data.name || t.unknownName;

        const toGB = (d) => {
            const date = new Date(d);
            return !isNaN(date)
                ? date.toLocaleDateString('en-GB', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                }).replace(',', '')
                : t.invalidDate;
        };

        const formattedLastUpdate = toGB(data.last_update);

        const nextUpdate = new Date(data.next_update);
        const now = new Date();
        let formattedNextUpdate = t.invalidDate;
        let countdown = null;
        let color = '#333';

        if (!isNaN(nextUpdate)) {
            formattedNextUpdate = toGB(nextUpdate);
            const diffMs = nextUpdate - now;
            const diffMinutes = diffMs / (1000 * 60);

            if (diffMs > 0 && diffMinutes <= 10) {
                color = '#4CAF50';
                countdown = Math.floor(diffMs / 1000);
            }
            if (diffMs < -3600000) {
                color = '#f20000';
            }
        }

        const styles = {
            row: { fontSize: 12, color: '#333', marginBottom: 2 },
            label: { fontWeight: 'bold', fontSize: 14 },
            next: { color, fontWeight: 'bold' },
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span style={styles.row}>
          <span style={styles.label}>{t.lastUpdate}</span> {formattedLastUpdate}
        </span>
                <span style={styles.row}>
          <span style={styles.label}>{t.nextUpdate}</span>{' '}
                    <span style={styles.next}>
            {formattedNextUpdate}
                        {countdown !== null && countdown > 0 && <CountdownTimer seconds={countdown} />}
          </span>
        </span>
            </div>
        );
    };

    const columns = [
        { label: t.colStatus, key: "status", format: formatStatus },
        { label: t.colName, key: "name", format: formatName },
        { label: t.colUpdates, key: "last_update", format: formatNameAndUpdates },
        { label: t.colContactGroup, key: "contacts_group_name" },
    ];

    const showField = (data, field) => {
        const key = field.key;
        const updated = { ...field };
        if ((key === "basic_auth_username" || key === "basic_auth_password") && String(data['basic_auth']) === "0") {
            updated.readonly = true;
        }
        return updated;
    };

    const fields = [
        {
            type: "tab",
            name: t.tabMain,
            image: "",
            showField,
            fields: [
                [
                    { label: t.fieldName, key: "name", default_value: "", showLabel: "top" },
                    { label: t.fieldContactGroup, key: "contact_group_id", default_value: "", type: "selectbox", data: contacts, showLabel: "top" }
                ],
                [
                    { label: t.basicAuth, key: "basic_auth", type: "selectbox", data: [{ id: '1', name: t.yes }, { id: '0', name: t.no }], default_value: "0", showLabel: "top" },
                    { label: t.login, key: "basic_auth_username", default_value: "", showLabel: "top" },
                    { label: t.password, key: "basic_auth_password", default_value: "", showLabel: "top" },
                ],
            ],
        },
    ];

    const actions = [
        { name: "edit", label: t.edit, controller: "crud" },
        { name: "delete", label: t.delete, controller: "crud" },
    ];

    const expired = user?.expired ?? 1;
    const disabled = expired
        ? { add: { disable: true, title: t.planExpired } }
        : { add: { disable: (totalData.pushtests - totalData.used_pushtests) < 1, title: t.noResources } };

    return (
        <BasicCRUD
            singleArgs={props?.singleArgs ?? false}
            title={t.title}
            subTitle={t.subTitle}
            homeURL={t.homeURL}
            addTitle={t.addTitle}
            disable={disabled}
            editTitle={t.editTitle}
            object={object}
            columns={columns}
            fields={fields}
            actions={actions}
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            response={response}
            setResponse={setResponse}
            ownHandler={true}
            deleteConfirmationDialog={{
                alert: t.delAlert,
                text: t.delText,
                title: t.delTitle,
                column: 'name',
            }}
        >
            {!props.singleArgs && <ProgressBar lang={lang} used={totalData.used_pushtests} allowed={totalData.pushtests} />}

            {response.status === 1 ? (
                <FadeAlert response={response} resetResponse={() => setResponse([])}>
                    {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                    {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                    {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                </FadeAlert>
            ) : null}
        </BasicCRUD>
    );
};

export default CustomTest;
